import { Injectable } from '@angular/core';
import _ from 'lodash';
import { IRequestOptions } from './request-options.interface';

@Injectable()
export class DataQueryService {
  public filterAndSliceData(
    data: any[],
    query: any,
    options: IRequestOptions
  ): any[] {
    return this.sliceData(this.filterData(this.sortData(data, options.sort), query), options);
  }

  public sortData(data: any[], sort: any) {
    if (!Array.isArray(sort) || sort.length == 0) {
      return data;
    }
    sort.forEach((s: string) => {
      if (s.startsWith('-')) {
        const prop = s.substring(1, s.length);
        data = data.sort((a, b) => {
          return _.get(a, prop, '').localeCompare(_.get(b, prop, ''));
        });
      } else {
        data = data.sort((a, b) => {
          return _.get(a, s, '').localeCompare(_.get(b, s, ''));
        }).reverse();
      }
    });
    return data;
  }

  public filterData(data: any[], query: any): any[] {
    if (!query || Object.keys(query).length === 0) {
      return data;
    }

    return data.filter((item) =>
      Object.keys(query).every((key) =>
        this.findInKey(
          key.indexOf('.') > 0 ? key.split('.') : [key],
          item,
          query[key]
        )
      )
    );
  }

  public sliceData(data: any[], options: IRequestOptions): any[] {
    if (!options || !options.limit) {
      return data;
    }

    const { page = 1, limit = 25 } = options;

    const startIdx = (page - 1) * limit;
    const endIdx = page * limit;

    return data.filter((item, idx) => idx >= startIdx && idx < endIdx);
  }

  public findInKey(keys: string[], element: any, val: any): boolean {
    if (keys.length === 1) {
      if (val.hasOwnProperty('$regex')) {
        return this.findRegex(keys[0], element, val);
      } else if (val.hasOwnProperty('$nin')) {
        return !this.findIn(keys[0], element, val);
      } else if (val.hasOwnProperty('$in')) {
        return this.findIn(keys[0], element, val);
      } else if (val.hasOwnProperty('$exists')) {
        return this.findExists(keys[0], element, val);
      } else {
        return this.findEquals(keys[0], element, val);
      }
    }

    const currentKey: any = keys.shift();
    if (!element.hasOwnProperty(currentKey)) {
      return false;
    }

    if (Array.isArray(element[currentKey])) {
      return element[currentKey].some((elItem: any) =>
        this.findInKey(keys, elItem, val)
      );
    } else {
      return this.findInKey(keys, element[currentKey], val);
    }
  }

  public findIn(key: any, element: any, val: any): boolean {
    const values: any[] = val.$nin || val.$in;
    if (values != null) {
      if (Array.isArray(element[key])) {
        return element[key].some((elItem: any) =>
          values.some(
            (valItem) => (valItem.$oid || valItem) === (elItem.$oid || elItem)
          )
        );
      } else {
        return values.some(
          (valItem) => valItem && (valItem.$oid || valItem) === element[key]
        );
      }
    } else {
      return false;
    }
  }

  public findRegex(key: any, element: any, val: any): boolean {
    const values = val.$regex;
    const options = val.$options || '';
    if (values != null) {
      return new RegExp(values, options).test(element[key]);
    } else {
      return false;
    }
  }

  public findExists(key: any, element: any, val: any): boolean {
    return val.$exists
      ? element.hasOwnProperty(key)
      : !element.hasOwnProperty(key);
  }

  public findEquals(key: any, element: any, val: any): boolean {
    if (Array.isArray(element)) {
      return element.some(
        (elItem) =>
          elItem.hasOwnProperty(key) && elItem[key] === (val.$oid || val)
      );
    } else {
      return element.hasOwnProperty(key) && element[key] === (val.$oid || val);
    }
  }
}
